import type { ShopperAiTagId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  MongoCustomerId,
  MongoUserId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { CustomerBasicInfo } from "@redotech/effect-api-shared/models/customer";
import type {
  SupportTicketAssigneeType,
  SupportTicketAssignmentUpdate,
  SupportTicketListItemResponse,
  SupportTicketPriority,
  SupportTicketSnoozeOptions,
  SupportTicketStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";

export type MutableSupportTicketListItem = {
  -readonly [K in keyof SupportTicketListItemResponse]: SupportTicketListItemResponse[K];
};

export type SnoozeParams =
  | { duration: Exclude<SupportTicketSnoozeOptions, "custom"> }
  | { duration: "custom"; until: Date }
  | null;

export function computeSnoozeUntil(
  option: Exclude<SupportTicketSnoozeOptions, "custom">,
  now = Date.now(),
): Date {
  switch (option) {
    case "1h":
      return new Date(now + 60 * 60 * 1000);
    case "4h":
      return new Date(now + 4 * 60 * 60 * 1000);
    case "24h":
      return new Date(now + 24 * 60 * 60 * 1000);
  }
}

export function applySupportTicketStatus(
  draft: MutableSupportTicketListItem,
  status: SupportTicketStatus,
): void {
  draft.status = status;
}

export function applySupportTicketReadState(
  draft: MutableSupportTicketListItem,
  readAt: Date | null,
): void {
  draft.readAt = readAt;
}

export function applySupportTicketSnooze(
  draft: MutableSupportTicketListItem,
  option: SnoozeParams,
): void {
  if (option === null) {
    draft.status = "OPEN";
    draft.snoozedUntil = null;
    return;
  }

  draft.status = "SNOOZED";
  draft.snoozedUntil =
    option.duration === "custom"
      ? option.until
      : computeSnoozeUntil(option.duration);
}

export function applySupportTicketPriority(
  draft: MutableSupportTicketListItem,
  priority: SupportTicketPriority | null,
): void {
  draft.priority = priority;
}

/**
 * Optimistically reflect a cosmetic email-subject change on the list row. The
 * row surfaces the subject as `emailSubject`; the change is persisted as a
 * `display_subject` override server-side via `toUpdateSupportTicketInput`.
 */
export function applySupportTicketEmailSubject(
  draft: MutableSupportTicketListItem,
  emailSubject: string,
): void {
  draft.emailSubject = emailSubject;
}

export function applySupportTicketAssignee(
  draft: MutableSupportTicketListItem,
  assigneeId: MongoUserId | null,
  assigneeType: SupportTicketAssigneeType | null = null,
): void {
  draft.mongoAssigneeId = assigneeId;
  draft.assigneeType = assigneeType;
}

/**
 * Maps an assignment update to the row fields that represent it, so callers can
 * optimistically reflect an assignment change on a list row. `assignee_type`
 * and `mongo_assignee_id` are independent columns, so both must be set together
 * to keep the row consistent (e.g. an AI assignment clears the user id).
 */
export function supportTicketAssignmentRowPatch(
  assignment: SupportTicketAssignmentUpdate,
): Pick<MutableSupportTicketListItem, "mongoAssigneeId" | "assigneeType"> {
  switch (assignment.assignmentType) {
    case "user":
      return { mongoAssigneeId: assignment.userId, assigneeType: "USER" };
    case "ai":
      return { mongoAssigneeId: null, assigneeType: "AI" };
    case "chatFlow":
      return { mongoAssigneeId: null, assigneeType: "CHAT_FLOW" };
    case "unassign":
      return { mongoAssigneeId: null, assigneeType: null };
  }
}

export function replaceSupportTicketTags(
  draft: MutableSupportTicketListItem,
  tagIds: ShopperAiTagId[],
): void {
  draft.tagIds = tagIds;
}

export type OptimisticCustomerInfo = Pick<
  CustomerBasicInfo,
  "displayName" | "email" | "phone"
>;

export function applySupportTicketCustomer(
  draft: MutableSupportTicketListItem,
  mongoCustomerId: MongoCustomerId | null,
  optimisticCustomer?: OptimisticCustomerInfo,
): void {
  draft.mongoCustomerId = mongoCustomerId;
  if (mongoCustomerId && optimisticCustomer) {
    draft.customer = {
      id: mongoCustomerId,
      displayName: optimisticCustomer.displayName,
      firstName: null,
      lastName: null,
      email: optimisticCustomer.email,
      phone: optimisticCustomer.phone,
      facebookAccounts: [],
      instagramAccounts: [],
    };
  } else {
    draft.customer = undefined;
  }
}
